import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Smartphone,
  BarChart3,
  Users,
  Package,
  Calendar,
  Shield,
  CheckCircle,
  Monitor,
} from 'lucide-react';
import Hero from '../components/Hero';
import FeatureCard from '../components/FeatureCard';
import TestimonialCard from '../components/TestimonialCard';
import StatsSection from '../components/StatsSection';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Home = () => {
  const features = [
    {
      icon: <Smartphone className="feature-icon" />,
      title: "Mobile & Web",
      description: "Use the mobile app on the go or the web app from any browser. One account, full access on both platforms.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <BarChart3 className="feature-icon" />,
      title: "Real-Time Analytics",
      description: "Get instant insights into your business performance with comprehensive dashboards and financial reports.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Users className="feature-icon" />,
      title: "Customer Management",
      description: "Maintain detailed customer profiles, track preferences, and manage loyalty programs effortlessly.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Package className="feature-icon" />,
      title: "Package Management",
      description: "Create and manage spa packages with flexible pricing, validity periods, and usage tracking.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <Calendar className="feature-icon" />,
      title: "Booking Management",
      description: "Streamline appointment scheduling with real-time room allocation and therapist assignment.",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <Shield className="feature-icon" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with Firebase authentication and real-time data synchronization.",
      color: "from-teal-500 to-green-500"
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Spa Owner",
      business: "Serenity Spa",
      content: "Wellness Pro transformed our business operations. The real-time analytics help us make data-driven decisions, and our staff loves the intuitive interface.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Rajesh Kumar",
      role: "Spa Manager",
      business: "Zen Wellness Center",
      content: "The package management system is a game-changer. We've increased customer retention by 40% and our customers love the flexible package options.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Deepika Singh",
      role: "Spa Director",
      business: "Luxury Spa Resort",
      content: "The financial tracking and reporting features have given us complete visibility into our business performance. Highly recommended!",
      rating: 5,
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face"
    }
  ];

  const benefits = [
    "30-day free trial with full access",
    "No setup fees or hidden costs",
    "24/7 customer support",
    "Regular feature updates",
    "Data backup and security",
    "Multi-location support"
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <Hero />

      {/* Features Section */}
      <section className="section-padding bg-slate-50">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              Everything You Need to Manage Your Spa
            </h2>
            <p className="text-small text-gray-600 max-w-3xl mx-auto">
              Our spa management solution works on mobile and web. One subscription gives you full access on both—streamline operations, increase revenue, and deliver exceptional customer experiences.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <FeatureCard {...feature} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Platforms: Mobile & Web */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              One Subscription, Two Ways to Work
            </h2>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              Choose the mobile app for on-the-go management or the web app for desktop—same features, same data, one login.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.a
              href={WEB_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-8 rounded-2xl border-2 border-gray-200 hover:border-emerald-400 hover:shadow-xl hover:shadow-emerald-500/15 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                <Monitor className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Web App</h3>
              <p className="text-gray-600 text-center text-sm mb-6">
                Full dashboard in your browser. Ideal for desktops and larger screens.
              </p>
              <span className="text-emerald-600 font-semibold group-hover:underline">Launch Web App →</span>
            </motion.a>
            <motion.a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-8 rounded-2xl border-2 border-gray-200 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-r from-cyan-500 to-indigo-500 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                <Smartphone className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Mobile App</h3>
              <p className="text-gray-600 text-center text-sm mb-6">
                Manage your spa from anywhere. Android app with 30-day free trial.
              </p>
              <span className="text-cyan-600 font-semibold group-hover:underline">Get on Google Play →</span>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Mobile App Preview */}
      <section className="section-padding bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
              `,
              backgroundSize: '400px 400px'
            }}
          ></div>
        </div>
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              className="text-white"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-large text-white mb-6">
                Manage Your Spa Anywhere—Mobile or Web
              </h2>
              <p className="text-small text-white/90 mb-8 max-w-2xl">
                Use the mobile app on the go or the web app at your desk. Same real-time data, bookings, payments, and analytics on both platforms.
              </p>
              <div className="flex flex-col gap-4 mb-8">
                <div className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Real-time booking management</span>
                </div>
                <div className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Instant payment processing</span>
                </div>
                <div className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Live business analytics</span>
                </div>
                <div className="flex items-center gap-3 text-white font-medium">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Customer feedback tracking</span>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <a 
                  href={WEB_APP_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-5 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
                >
                  <Monitor size={20} />
                  Launch Web App
                </a>
                <a 
                  href={PLAY_STORE_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] leading-tight">GET IT ON</span>
                    <span className="text-lg font-bold leading-tight">Google Play</span>
                  </div>
                </a>
              </div>
            </motion.div>
            <motion.div
              className="flex justify-center items-center"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="w-80 h-96 bg-gray-900 rounded-3xl p-5 shadow-2xl">
                <div className="w-full h-full rounded-2xl overflow-hidden bg-white">
                  <img 
                    src="/spa on the go.jpg" 
                    alt="Wellness Pro Mobile App"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              Loved by Spa Professionals Worldwide
            </h2>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              Join thousands of spa owners and managers who have transformed their business with Wellness Pro.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <TestimonialCard {...testimonial} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
              `,
              backgroundSize: '400px 400px'
            }}
          ></div>
        </div>
        <div className="container relative z-10">
          <motion.div
            className="text-center text-white"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-white mb-6">
              Ready to Transform Your Spa Business?
            </h2>
            <p className="text-small text-white/90 mb-8 max-w-2xl mx-auto">
              Start your free trial on mobile or web and experience the difference professional spa management can make.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 text-white font-medium"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>{benefit}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
              <a 
                href={WEB_APP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                <Monitor size={20} />
                Launch Web App
              </a>
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">GET IT ON</span>
                  <span className="text-lg font-bold leading-tight">Google Play</span>
                </div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
